import React from "react";
import logo from "./ui/logo.png";

const perks = [
  "Weekday daily 2–3 micro exercises via email",
  "Business vocabulary & phrases",
  "Mini dialogues & quick checks (60–90 sec.)",
  "End-of-month 10-question quiz (optional)",
];

const plans = [
  { name: "1 Month", price: "12 €", ribbon: "Trial", popular: false, cta: { label: "Choose 1 Month", href: "/1m" } },
  { name: "3 Months", price: "29 €", ribbon: "Popular", popular: true, cta: { label: "Choose 3 Months", href: "/3m" } },
  { name: "12 Months", price: "89 €", ribbon: "Save 35%", popular: false, cta: { label: "Choose 12 Months", href: "/12m" } },
];

function Badge({ children }) {
  return (
    <span className="inline-flex items-center rounded-full border border-brand-gray px-2.5 py-0.5 text-xs font-medium text-brand-gray bg-white/60 backdrop-blur">
      {children}
    </span>
  );
}

function CheckIcon() {
  return (
    <svg className="h-4 w-4 flex-none" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
      <path fillRule="evenodd" d="M16.704 5.29a1 1 0 0 1 .006 1.414l-7.25 7.375a1 1 0 0 1-1.436.01L3.29 9.69A1 1 0 1 1 4.71 8.27l3.17 3.17 6.536-6.64a1 1 0 0 1 1.288-.51z" clipRule="evenodd"/>
    </svg>
  );
}

export default function LandingPricing() {
  return (
    <main className="min-h-screen bg-brand-light">
      {/* Hero */}
      <section className="relative mx-auto max-w-6xl px-6 pb-8 pt-16 text-center">
        <img src={logo} alt="Logo" className="mx-auto mb-6 h-24 w-auto" />

        <div className="mx-auto max-w-3xl text-center">
          <div className="mb-3 flex items-center justify-center gap-2">
            <Badge>Start: 1st weekday of each month</Badge>
            <Badge>100% Micro-Learning</Badge>
          </div>
          <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl">
            Improve your <span className="bg-yellow-200 px-1">Business English</span> every day
          </h1>
          <p className="mt-4 text-lg text-brand-gray">
            Daily "knowledge checker" via email – 60 to 90 seconds per day. Short impulses, real impact.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a href="#pricing" className="rounded-2xl bg-brand-middark px-5 py-3 text-white font-semibold shadow-lg transition hover:shadow-xl">
              View Plans
            </a>
            <a href="#challenge" className="rounded-2xl border border-brand-gray bg-white px-5 py-3 text-brand-dark shadow-sm transition hover:shadow">
              View Monthly Challenge
            </a>
          </div>
        </div>
      </section>

      {/* Social proof / perks */}
      <section className="mx-auto max-w-5xl px-6 pb-6">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {perks.map((perk) => (
            <div key={perk} className="rounded-2xl border border-brand-light bg-white p-4 shadow-sm">
              <div className="mb-2 flex items-center gap-2">
                <CheckIcon />
                <span className="text-sm font-semibold">Short & effective</span>
              </div>
              <p className="text-sm text-brand-gray">{perk}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="mx-auto max-w-6xl px-6 py-12">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold sm:text-4xl">Choose Your Plan</h2>
          <p className="mt-3 text-brand-gray">
            Start collectively on the <strong>1st weekday of the month</strong>. Cancel anytime.
          </p>
        </div>

        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {plans.map((p) => (
            <div
              key={p.name}
              className={
                "relative rounded-3xl border bg-white p-6 shadow-sm transition hover:shadow-md " +
                (p.popular ? "border-brand-middark ring-2 ring-brand-middark" : "border-brand-light")
              }
            >
              {p.ribbon && (
                <div className="absolute -right-2 -top-2 rounded-full bg-brand-middark px-3 py-1 text-xs font-semibold text-white shadow">
                  {p.ribbon}
                </div>
              )}
              <h3 className="text-xl font-bold text-brand-dark">{p.name}</h3>
              <div className="mt-2 flex items-baseline gap-2">
                <span className="text-3xl font-extrabold text-brand-dark">{p.price}</span>
                <span className="text-sm text-brand-gray">incl. VAT</span>
              </div>
              <ul className="mt-5 space-y-2 text-sm text-brand-gray">
                <li className="flex items-start gap-2"><CheckIcon /> Daily knowledge checker (weekdays)</li>
                <li className="flex items-start gap-2"><CheckIcon /> Access to Monthly Challenge</li>
                <li className="flex items-start gap-2"><CheckIcon /> Email support & short explanations</li>
              </ul>
              <a
                href={p.cta.href}
                className={
                  "mt-6 block w-full rounded-2xl px-5 py-3 text-center text-sm font-semibold shadow transition " +
                  (p.popular ? "bg-brand-middark text-white hover:shadow-lg" : "border border-brand-gray bg-white text-brand-dark hover:shadow")
                }
              >
                {p.cta.label}
              </a>
              <p className="mt-3 text-xs text-brand-gray">
                Starts on the 1st weekday. Next start date confirmed by email after signup.
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8 rounded-2xl border border-brand-light bg-white p-5 text-sm text-brand-gray">
          <p>
            <strong>Note:</strong> If you have already completed your Six-Pack, sign up via Tally. We will send a welcome email before the start.
          </p>
          <div className="mt-3 flex flex-wrap gap-3">
            <a href="https://tally.so/r/your-tally-form-id" className="rounded-xl border border-brand-gray bg-white px-4 py-2 text-brand-dark shadow-sm hover:shadow">
              Go to Tally Form
            </a>
            <a href="#faq" className="rounded-xl bg-brand-middark px-4 py-2 font-semibold text-white shadow hover:shadow-lg">
              FAQ
            </a>
          </div>
        </div>
      </section>

      {/* Monthly Challenge */}
      <section id="challenge" className="mx-auto max-w-6xl px-6 py-12">
        <div className="grid items-center gap-8 lg:grid-cols-2">
          <div>
            <h2 className="text-3xl font-bold sm:text-4xl">Monthly Challenge: 10-Question Quiz</h2>
            <p className="mt-3 text-brand-gray">
              At the end of the month we test the key points again – compact, playful, and with a leaderboard. The <strong>top 3</strong> each win a <strong>45-minute Business English coaching session</strong> with me.
            </p>
            <ul className="mt-5 space-y-2 text-sm text-brand-gray">
              <li className="flex items-start gap-2"><CheckIcon /> Questions based on your daily emails of the month</li>
              <li className="flex items-start gap-2"><CheckIcon /> Participation voluntary – 5–7 minutes</li>
              <li className="flex items-start gap-2"><CheckIcon /> Fair play: one submission per person (email verification)</li>
            </ul>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="https://tally.so/r/your-tally-quiz-id" className="rounded-2xl bg-brand-middark px-5 py-3 text-white font-semibold shadow hover:shadow-lg">
                Go to Monthly Challenge
              </a>
              <a href="#rules" className="rounded-2xl border border-brand-gray bg-white px-5 py-3 text-brand-dark shadow-sm hover:shadow">
                Rules & Selection
              </a>
            </div>
          </div>

          <div className="rounded-3xl border border-brand-light bg-white p-6 shadow-sm">
            <h3 className="text-lg font-semibold">Sample Leaderboard</h3>
            <div className="mt-4 space-y-3">
              {[{n:"Alex",s:10},{n:"Sam",s:9},{n:"Mina",s:9},{n:"Chris",s:8},{n:"Pat",s:8}].map((row, i) => (
                <div key={row.n} className="flex items-center justify-between rounded-2xl border border-brand-light bg-brand-light/20 px-4 py-3">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-sm font-semibold shadow">
                      {i+1}
                    </span>
                    <span className="font-medium">{row.n}</span>
                  </div>
                  <span className="text-sm text-brand-gray">{row.s}/10</span>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-brand-gray/70">Demo only. Real leaderboard appears after the challenge ends.</p>
          </div>
        </div>
      </section>

      {/* Rules */}
      <section id="rules" className="mx-auto max-w-5xl px-6 pb-12">
        <div className="rounded-3xl border border-brand-light bg-white p-6 shadow-sm">
          <h3 className="text-xl font-bold">Rules & Selection</h3>
          <ol className="mt-3 list-decimal space-y-2 pl-5 text-sm text-brand-gray">
            <li>One participation per person (email used for verification).</li>
            <li>If tied, the <em>earlier submission</em> decides.</li>
            <li>The top three receive a 45-minute coaching session. Scheduling within 14 days.</li>
            <li>Only first names are displayed on the leaderboard (privacy).</li>
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="mx-auto max-w-6xl px-6 pb-16">
        <div className="grid gap-6 md:grid-cols-2">
          <div className="rounded-3xl border border-brand-light bg-white p-6 shadow-sm">
            <h4 className="text-lg font-semibold">When does my subscription start?</h4>
            <p className="mt-2 text-sm text-brand-gray">
              Always on the <strong>1st weekday of the month</strong>. We will remind you by email shortly before.
            </p>
          </div>
          <div className="rounded-3xl border border-brand-light bg-white p-6 shadow-sm">
            <h4 className="text-lg font-semibold">Can I cancel?</h4>
            <p className="mt-2 text-sm text-brand-gray">
              Yes, anytime with a single click in any email. You can also upgrade to a longer plan.
            </p>
          </div>
          <div className="rounded-3xl border border-brand-light bg-white p-6 shadow-sm">
            <h4 className="text-lg font-semibold">How long are the daily emails?</h4>
            <p className="mt-2 text-sm text-brand-gray">60–90 seconds. One question, a mini-dialog, or a phrase.</p>
          </div>
          <div className="rounded-3xl border border-brand-light bg-white p-6 shadow-sm">
            <h4 className="text-lg font-semibold">How do I select coaching?</h4>
            <p className="mt-2 text-sm text-brand-gray">
              We contact the three winners directly with a booking link. Coaching online via Zoom/Meet.
            </p>
          </div>
        </div>

        {/* Footer CTA */}
        <div id="tally-signup" className="mt-10 rounded-3xl border border-brand-light bg-brand-dark p-6 text-white">
          <h3 className="text-2xl font-bold">Ready? Sign up now & start on the 1st weekday</h3>
          <p className="mt-2 text-brand-light">
            Sign up via Tally. We will confirm your start date by email.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            <a href="https://tally.so/r/your-tally-form-id" target="_blank" rel="noreferrer" className="rounded-2xl bg-white px-5 py-3 text-brand-dark shadow hover:shadow-lg">
              Open Tally Form
            </a>
            <a href="#pricing" className="rounded-2xl border border-white/30 px-5 py-3 text-white">
              Choose Plan
            </a>
          </div>
          <p className="mt-3 text-xs text-brand-light/70">Links adjusted: replace with your real checkout and Tally URLs.</p>
        </div>
      </section>
    </main>
  );
}
